import { useAuth } from "@canucci/base/auth";
import type { MfaStepUpErrorDetail } from "@canucci/base/auth/mfaStepUpError";
import { useMfaCompliance } from "@canucci/base/auth/useMfaCompliance";
import { env } from "@canucci/base/env";
import { Alert, Button, Container, Loader, Stack, Text, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { buildMfaEnrollmentUrl } from "@/services/mfaRemediation";

export default function MfaRemediationPage({ detail, onRetry }: { detail: MfaStepUpErrorDetail; onRetry: () => void }) {
  const { user, getMfaEnrollmentFactors, logout } = useAuth();
  const { t } = useTranslation("pages");
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const impersonated = detail.reason === "impersonation_not_supported";
  const { verdict, checking, retry } = useMfaCompliance({
    user,
    mfaRequired: true,
    organizationId: detail.organization_id,
    getMfaEnrollmentFactors,
    enabled: !impersonated,
  });
  const enrollmentUrl = buildMfaEnrollmentUrl(env.VITE_APP_SITE_URL, detail.organization_id);
  const signIn = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await logout({ preserveDeepLink: location.pathname + location.search });
      queryClient.clear();
      navigate("/login", { replace: true });
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Container size="sm" py="xl">
      <Stack gap="md">
        <Title order={1}>{t("organizationMfa.title")}</Title>
        <Text>{t(impersonated ? "organizationMfa.impersonation" : "organizationMfa.description")}</Text>
        {!impersonated && (
          <>
            {checking && <Loader />}
            {verdict?.status === "needs_enrollment" &&
              (enrollmentUrl ? (
                <>
                  <Text>{t("organizationMfa.enrollmentInstructions")}</Text>
                  <Button component="a" href={enrollmentUrl} target="_blank" rel="noopener noreferrer">
                    {t("organizationMfa.enroll")}
                  </Button>
                </>
              ) : (
                <Alert color="red">{t("organizationMfa.unavailable")}</Alert>
              ))}
            {(verdict?.status === "unavailable" || failed) && (
              <Alert color="red">{t("organizationMfa.unavailable")}</Alert>
            )}
            <Button loading={busy} onClick={signIn}>
              {t("organizationMfa.signIn")}
            </Button>
          </>
        )}
        <Button
          variant="default"
          onClick={() => {
            retry();
            void queryClient.invalidateQueries();
            onRetry();
          }}
        >
          {t("organizationMfa.retry")}
        </Button>
      </Stack>
    </Container>
  );
}
